import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Package, Truck, Clock, Shield, MapPin, FileText } from "lucide-react";

const TransportColisFranceMaroc = () => {
  return (
    <>
      <Helmet>
        <title>Transport de Colis France Maroc : Guide Complet 2025 | GoBledPro</title>
        <meta 
          name="description" 
          content="Tout savoir sur le transport de colis entre la France et le Maroc : options, délais, tarifs, réglementation douanière et conseils pratiques pour un envoi réussi." 
        />
        <link rel="canonical" href="https://gobledpro.com/transport-colis-france-maroc" />
        <meta property="og:title" content="Transport de Colis France Maroc : Guide Complet 2025 | GoBledPro" />
        <meta property="og:description" content="Tout savoir sur le transport de colis entre la France et le Maroc : options, délais, tarifs, réglementation douanière et conseils pratiques." />
        <meta property="og:image" content="https://gobledpro.com/og-transport-colis.png" />
        <meta property="og:url" content="https://gobledpro.com/transport-colis-france-maroc" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="GoBledPro" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://gobledpro.com/og-transport-colis.png" />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-background pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block bg-primary/10 text-primary text-sm font-medium px-4 py-1.5 rounded-full mb-6">
                Guide Informatif
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6 leading-tight">
                Transport de Colis France – Maroc : Le Guide Complet
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Découvrez toutes les options pour envoyer vos colis entre la France et le Maroc, 
                les délais à prévoir, la réglementation douanière et nos conseils pour un envoi serein.
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto prose prose-lg prose-headings:font-heading prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground">
              
              {/* Introduction */}
              <section className="mb-12">
                <p className="text-lg leading-relaxed">
                  Chaque année, des milliers de personnes envoient des colis entre la France et le Maroc : 
                  cadeaux pour la famille, effets personnels, achats en ligne ou documents importants. 
                  Face à la diversité des offres et des contraintes réglementaires, il est essentiel de 
                  bien s'informer avant d'expédier.
                </p>
              </section>

              {/* Section 1 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Les différentes options de transport</h2>
                </div>
                
                <h3 className="text-xl font-semibold mt-6 mb-3">Transport routier</h3>
                <p>
                  Le transport routier reste la solution la plus répandue pour les envois France-Maroc. 
                  Des transporteurs professionnels effectuent des liaisons régulières, généralement via 
                  l'Espagne et le détroit de Gibraltar. Cette option offre un bon rapport qualité-prix 
                  pour les colis de taille moyenne à importante.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3">Transport aérien</h3>
                <p>
                  Plus rapide mais généralement plus coûteux, le fret aérien convient aux envois urgents 
                  ou de faible volume. Les délais varient entre 2 et 5 jours selon les compagnies et 
                  les aéroports de destination.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3">Transport maritime</h3>
                <p>
                  Économique pour les gros volumes, le fret maritime est adapté aux déménagements 
                  ou aux envois commerciaux importants. Les délais sont plus longs (1 à 3 semaines) 
                  mais les tarifs au kilo restent compétitifs.
                </p>
              </section>

              {/* Section 2 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Délais de livraison à prévoir</h2>
                </div>
                
                <p>
                  Les délais varient selon le mode de transport choisi et la destination finale au Maroc :
                </p>
                
                <ul className="space-y-2 mt-4">
                  <li><strong>Transport routier :</strong> 5 à 10 jours ouvrés</li>
                  <li><strong>Transport aérien :</strong> 2 à 5 jours ouvrés</li>
                  <li><strong>Transport maritime :</strong> 10 à 21 jours ouvrés</li>
                </ul>

                <p className="mt-4">
                  Ces délais sont indicatifs et peuvent varier en fonction des périodes de l'année 
                  (notamment avant l'été et les fêtes), des formalités douanières et de l'accessibilité 
                  de la destination.
                </p>
              </section>

              {/* Section 3 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Réglementation et formalités douanières</h2>
                </div>
                
                <h3 className="text-xl font-semibold mt-6 mb-3">Documents requis</h3>
                <p>
                  Pour tout envoi vers le Maroc, il est recommandé de préparer :
                </p>
                <ul className="space-y-2 mt-4">
                  <li>Une facture ou liste détaillée du contenu</li>
                  <li>La valeur déclarée des articles</li>
                  <li>Les coordonnées complètes de l'expéditeur et du destinataire</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3">Produits interdits ou réglementés</h3>
                <p>
                  Certains articles sont soumis à des restrictions ou interdits à l'importation au Maroc :
                </p>
                <ul className="space-y-2 mt-4">
                  <li>Produits alimentaires périssables (sauf conditions spécifiques)</li>
                  <li>Médicaments sans ordonnance médicale</li>
                  <li>Contrefaçons et articles portant atteinte aux marques</li>
                  <li>Matériel électronique sous certaines conditions</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3">Droits de douane</h3>
                <p>
                  Les envois personnels peuvent être soumis à des droits de douane à l'arrivée au Maroc, 
                  calculés sur la valeur déclarée. Les taux varient selon la nature des produits. 
                  Il est conseillé de se renseigner auprès des services douaniers marocains pour 
                  connaître les franchises applicables.
                </p>
              </section>

              {/* Section 4 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Package className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Conseils pour bien préparer votre colis</h2>
                </div>
                
                <ul className="space-y-3 mt-4">
                  <li>
                    <strong>Emballage solide :</strong> Utilisez des cartons renforcés et du matériel 
                    de calage pour protéger vos articles pendant le transport.
                  </li>
                  <li>
                    <strong>Étiquetage clair :</strong> Indiquez lisiblement les coordonnées du 
                    destinataire, y compris un numéro de téléphone marocain valide.
                  </li>
                  <li>
                    <strong>Liste du contenu :</strong> Préparez une liste détaillée du contenu 
                    pour faciliter les formalités douanières.
                  </li>
                  <li>
                    <strong>Assurance :</strong> Pour les objets de valeur, envisagez une assurance 
                    transport couvrant la perte ou les dommages.
                  </li>
                </ul>
              </section>

              {/* Section 5 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Principales destinations desservies</h2>
                </div>
                
                <p>
                  Les liaisons France-Maroc couvrent l'ensemble du territoire marocain, avec des 
                  dessertes particulièrement fréquentes vers :
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mt-6 not-prose">
                  <div className="bg-secondary/30 rounded-lg p-4">
                    <h4 className="font-semibold text-foreground mb-2">Grandes villes</h4>
                    <p className="text-sm text-muted-foreground">
                      Casablanca, Rabat, Marrakech, Fès, Tanger, Agadir, Meknès, Oujda
                    </p>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-4">
                    <h4 className="font-semibold text-foreground mb-2">Régions</h4>
                    <p className="text-sm text-muted-foreground">
                      Nord (Tétouan, Nador), Centre (Béni Mellal), Sud (Laâyoune, Dakhla)
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 6 */}
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold m-0">Choisir un transporteur fiable</h2>
                </div>
                
                <p>
                  Pour garantir la sécurité de vos envois, privilégiez des transporteurs qui offrent :
                </p>
                
                <ul className="space-y-3 mt-4">
                  <li>Une expérience avérée sur le corridor France-Maroc</li>
                  <li>Un suivi de colis transparent</li>
                  <li>Une assurance transport incluse ou proposée</li>
                  <li>Des avis clients vérifiables</li>
                  <li>Une communication claire sur les délais et tarifs</li>
                </ul>

                <p className="mt-6">
                  Des plateformes de mise en relation permettent aujourd'hui de comparer les offres 
                  et de sélectionner des transporteurs professionnels selon vos critères.
                </p>
              </section>

              {/* Conclusion */}
              <section className="bg-secondary/20 rounded-2xl p-8 not-prose">
                <h2 className="text-xl font-bold text-foreground mb-4">En résumé</h2>
                <p className="text-muted-foreground mb-4">
                  Le transport de colis entre la France et le Maroc nécessite une bonne préparation : 
                  choix du mode de transport adapté à vos besoins, respect des formalités douanières, 
                  et sélection d'un transporteur fiable. En anticipant ces étapes, vous maximisez 
                  les chances d'un envoi réussi et serein.
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <a href="/transport-bagages-france-maroc" className="text-primary hover:underline">Transport de bagages personnels et valises France ⇄ Maroc →</a>
                  <a href="/transport-colis-maroc-fiable" className="text-primary hover:underline">Transport fiable →</a>
                  <a href="/transporteurs-verifies-maroc" className="text-primary hover:underline">Transporteurs vérifiés →</a>
                  <a href="/faq-transport-colis-maroc" className="text-primary hover:underline">FAQ →</a>
                </div>
              </section>

            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
};

export default TransportColisFranceMaroc;
